// Attendance Regularisation — employee view (Phase A1).
// The safety net for a genuinely-worked day that couldn't be recorded (office
// WiFi down, portal/device issue, forgot). The employee raises a request for a
// PAST working day with a reason; an admin approves, which records the day as a
// proper (paid) attendance row clearly marked as admin-regularised — never a
// WiFi-verified in-office punch.
//
// Mirrors WfhPage.jsx: info card + request modal + a table of my requests with
// a cancel action on pending rows. Changes no attendance/gate logic; it only
// drives the new /api/attendance/regularisation/* endpoints.

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, REQUEST_TONES } from "../../Components/ui";
import { Backdrop, Modal, Header, Body, Footer, Field, inputStyle, btnPrimary as mBtnPrimary, btnGhost as mBtnGhost } from "../Leaves/ApplyLeaveModal";

// Shared approval-lifecycle tones (see Components/ui/statusTones.js). Was a
// private copy of four hardcoded light-theme pairs, duplicated across five
// pages and unreadable-looking in dark mode.
const STATUS_STYLE = REQUEST_TONES;

export default function RegularisationPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useShellHeader({ title: "Attendance Regularisation" }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api("/attendance/regularisation/my");
      setRows(Array.isArray(r) ? r : (r.regularisations || r.rows || []));
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function cancel(r) {
    if (!window.confirm("Withdraw this pending regularisation request?")) return;
    try {
      await api(`/attendance/regularisation/${r._id}`, { method: "DELETE" });
      toast({ title: "Request withdrawn.", status: "success" });
      load();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>🛠️ Attendance Regularisation</h2>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
            Worked a day but it wasn't recorded? Raise a request so it still counts.
          </div>
        </div>
        <button onClick={() => setOpen(true)} style={btnPrimary}>+ Request Regularisation</button>
      </div>

      {/* How it works info card */}
      <div style={{
        background: "linear-gradient(135deg, var(--bg-surface) 0%, var(--tone-success-bg) 100%)",
        border: "1px solid var(--tone-success-br)",
        borderRadius: 12, padding: "16px 20px", color: "var(--tone-success-fg)",
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>When to use this</div>
        <ul style={{ margin: 0, paddingLeft: 22, fontSize: 13, lineHeight: 1.7 }}>
          <li>Use it for a <strong>past working day you actually worked</strong> but couldn't check in — office WiFi/network down, a portal or device issue, or you forgot.</li>
          <li>Pick the date, add the hours you worked, and explain what happened.</li>
          <li>An admin reviews it. Once approved, that day is recorded as a normal paid day.</li>
          <li>You can raise a request for a day in <strong>this month or last month</strong>. For older days, ask an admin directly.</li>
        </ul>
      </div>

      <Card padding="none">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr><Th>Date</Th><Th>Proposed In</Th><Th>Proposed Out</Th><Th>Reason</Th><Th>Status</Th><Th>Admin Note</Th><Th>Submitted</Th><Th>Action</Th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan={8} style={{ textAlign: "center", color: "var(--text-tertiary)" }}>Loading…</Td></tr>
              ) : rows.length === 0 ? (
                <tr><Td colSpan={8} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No regularisation requests yet.</Td></tr>
              ) : rows.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                return (
                  <tr key={r._id}>
                    <Td className="tabular">{fmtDate(r.workDate)}</Td>
                    <Td className="tabular">{fmtTime(r.proposedCheckIn)}</Td>
                    <Td className="tabular">{fmtTime(r.proposedCheckOut)}</Td>
                    <Td style={{ maxWidth: 320, color: "var(--text-secondary)" }}>{r.reason || "—"}</Td>
                    <Td>
                      <span style={{
                        background: s.bg, color: s.co, padding: "2px 10px",
                        borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      }}>{r.status}</span>
                    </Td>
                    <Td style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{r.adminNote || "—"}</Td>
                    <Td className="tabular" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}</Td>
                    <Td>{r.status === "pending" ? (
                      <button onClick={() => cancel(r)} style={btnDanger}>Withdraw</button>
                    ) : "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <RequestModal open={open} onClose={() => setOpen(false)} onSubmitted={() => { setOpen(false); load(); }} />
    </div>
  );
}

// Default window hint: yesterday (a past day). The server enforces the real
// bound (this month or last month, not before joining, no existing check-in).
function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function RequestModal({ open, onClose, onSubmitted }) {
  const toast = useToast();
  const [workDate, setWorkDate] = useState("");
  const [checkIn, setCheckIn] = useState("10:00");
  const [checkOut, setCheckOut] = useState("19:00");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const today = todayIso();

  if (!open) return null;

  async function submit() {
    if (!workDate || !reason.trim()) { toast({ title: "Date and reason are required.", status: "warning" }); return; }
    if (workDate >= today) { toast({ title: "Pick a past day. For today, just check in normally.", status: "warning" }); return; }
    setBusy(true);
    try {
      await api("/attendance/regularisation", {
        method: "POST",
        body: JSON.stringify({
          workDate,
          proposedCheckIn: toHHMMSS(checkIn),
          proposedCheckOut: toHHMMSS(checkOut),
          reason,
        }),
      });
      toast({ title: "Request submitted. Awaiting admin approval.", status: "success" });
      setWorkDate(""); setCheckIn("10:00"); setCheckOut("19:00"); setReason("");
      onSubmitted && onSubmitted();
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    } finally { setBusy(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <Modal>
        <Header title="🛠️ Request Attendance Regularisation" onClose={onClose} />
        <Body>
          <Field label="MISSED DATE *">
            <input type="date" value={workDate} max={yesterdayIso()} onChange={(e) => setWorkDate(e.target.value)} style={inputStyle} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="CHECK-IN"><input type="time" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} style={inputStyle} /></Field>
            <Field label="CHECK-OUT"><input type="time" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} style={inputStyle} /></Field>
          </div>
          <Field label="WHAT HAPPENED? *">
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Office WiFi was down all morning / portal wouldn't load / forgot to check in. Be specific — an admin will review this."
              style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
            />
          </Field>
          <div style={{
            background: "var(--tone-success-bg)", border: "1px solid var(--tone-success-br)",
            borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--tone-success-fg)",
          }}>
            ✓ Approved regularisations are recorded as a normal paid day, clearly marked as admin-regularised. They are <strong>not</strong> shown as an office-WiFi check-in.
          </div>
        </Body>
        <Footer>
          <button onClick={onClose} style={mBtnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={mBtnPrimary} disabled={busy}>{busy ? "Submitting…" : "Submit Request"}</button>
        </Footer>
      </Modal>
    </Backdrop>
  );
}

function toHHMMSS(t) { if (!t) return undefined; return t.length === 5 ? `${t}:00` : t; }
function fmtTime(t) { return t ? String(t).slice(0, 5) : "—"; }
function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }

const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-primary)" };
const btnPrimary = {
  background: "var(--accent-primary)", color: "white", border: "none",
  padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const btnDanger = {
  background: "transparent", color: "var(--tone-danger-fg)",
  padding: "4px 10px", border: "1px solid var(--border-default)", borderRadius: 6,
  fontSize: 12, cursor: "pointer", fontWeight: 500,
};
function Th({ children }) { return <th style={thStyle}>{children}</th>; }
function Td({ children, style, className, colSpan }) { return <td colSpan={colSpan} style={{ ...tdBase, ...style }} className={className}>{children}</td>; }
